/**
 * ZoneStatusPanel — right sidebar shown when a zone is selected on the map.
 *
 * Matches prototype map-dashboard.jsx right panel exactly.
 * 340px wide, full-height flex column.
 * All values are anonymous aggregates — no PII.
 */

import { useNavigate } from 'react-router-dom';
import type { DensitySnapshot, Zone, ZoneType } from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import { useFlags } from '../hooks/useFlags';
import { type ColorPalette, FONT_SIZE, LETTER_SPACING, Icons, KLabel, StatusDot, Pill, Btn, SIZE } from '@vigilo/design-system';
import type { CurrentZoneMetrics } from '../hooks/useLiveZoneMetrics';
import { alertCountColor, loadColor, resolveZoneStatus, trendColor as trendToneColor, zoneStatusColor, zoneStatusLabel } from '../utils/zone-status';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ZoneStatusPanelProps {
  zone:         Zone;
  snap:         DensitySnapshot | undefined;
  metric?:      CurrentZoneMetrics;
  /** Real capacity-utilisation samples for this zone, oldest first, covering the last 60 minutes. */
  densitySparkline?: number[];
  /** Change in utilisation (pct points) vs ~5 minutes ago, or null if no sample from then exists. */
  surgeRate5Min?: number | null;
  /** True when snap is a historical playback point with no real trend data. */
  trendUnavailable?: boolean;
  projectionStatus?: 'simulation' | 'fresh' | 'stale' | 'missing' | 'loading' | 'error';
  operationName?: string | null;
  /** Why this zone was opened automatically on landing (e.g. "2 active alerts · 91% utilised"). */
  priorityNote?: string | null;
  activeAlertCount?: number;
  flaggedTokenCount?: number;
  C:            ColorPalette;
  onClose:      () => void;
  zones:        Zone[];
  onZoneChange: (id: string) => void;
}

// ── Zone type lookups ─────────────────────────────────────────────────────────

const CROWD_ZONE_TYPE_CODES: Partial<Record<ZoneType, string>> = {
  entry_exit_gate:        'GATE',
  standing_area:          'STD',
  seated_section:         'SEC',
  vip_area:               'VIP',
  circulation_corridor:   'COR',
  service_area:           'SVC',
  assembly_point:         'ASM',
  restricted_zone:        'RST',
  perimeter:              'PER',
  temporary_ops:          'TMP',
};

const CROWD_ZONE_TYPE_LABELS: Partial<Record<ZoneType, string>> = {
  entry_exit_gate:        'Entry / Exit Gate',
  standing_area:          'Standing Area',
  seated_section:         'Seated Section',
  vip_area:               'VIP Area',
  circulation_corridor:   'Circulation',
  service_area:           'Service Area',
  assembly_point:         'Assembly Point',
  restricted_zone:        'Restricted',
  perimeter:              'Perimeter',
  temporary_ops:          'Temporary',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function densityColor(util: number, C: ColorPalette): string {
  return loadColor(util, C);
}

function zoneTypeCode(type: ZoneType): string {
  return CROWD_ZONE_TYPE_CODES[type] ?? 'ZONE';
}

function zoneTypeLabel(type: ZoneType): string {
  return CROWD_ZONE_TYPE_LABELS[type] ?? 'Configured Zone';
}

function projectionStatusLabel(status: ZoneStatusPanelProps['projectionStatus']): string {
  if (status === 'simulation') return 'Simulation';
  if (status === 'fresh') return 'Live data';
  if (status === 'stale') return 'Stale data';
  if (status === 'loading') return 'Loading data';
  if (status === 'error') return 'Data unavailable';
  return 'No data';
}

function projectionStatusColor(status: ZoneStatusPanelProps['projectionStatus'], C: ColorPalette): string {
  if (status === 'fresh') return C.green;
  if (status === 'error') return C.ember;
  if (status === 'stale' || status === 'missing') return C.amber;
  return C.muted;
}

// ── Sparkline ─────────────────────────────────────────────────────────────────

function ZoneSparkline({ data, color, C }: { data: number[]; color: string; C: ColorPalette }) {
  const w = 300, h = 56;
  const max = 100;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * (h - 4) - 2}`).join(' ');
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 5,
      padding: '8px 10px',
      marginBottom: 4,
    }}>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        {[25, 50, 75].map(y => (
          <line
            key={y}
            x1="0" y1={h - (y / 100) * (h - 4) - 2}
            x2={w} y2={h - (y / 100) * (h - 4) - 2}
            stroke={C.border2} strokeDasharray="2,3" strokeWidth="0.5"
          />
        ))}
        <line
          x1="0" y1={h - 0.85 * (h - 4) - 2}
          x2={w} y2={h - 0.85 * (h - 4) - 2}
          stroke={C.ember} strokeDasharray="3,2" strokeWidth="0.6" opacity="0.6"
        />
        <polyline points={`0,${h} ${pts} ${w},${h}`} fill={`${color}22`} />
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        <circle
          cx={w}
          cy={h - (data[data.length - 1] / max) * (h - 4) - 2}
          r="2.5" fill={color} stroke={C.surface} strokeWidth="1.5"
        />
      </svg>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 9.5, lineHeight: '11.9px', color: C.muted, fontFamily: 'monospace', marginTop: 3,
      }}>
        <span>-60m</span><span>-30m</span><span>now</span>
      </div>
    </div>
  );
}

// ── MiniStat ──────────────────────────────────────────────────────────────────

function MiniStat({ label, value, sub, tone, C, color }: {
  label: string; value: string; sub: string;
  tone: 'normal' | 'warning' | 'alert' | 'alertCount';
  C: ColorPalette;
  color?: string;
}) {
  const valueColor = color ?? (tone === 'warning' ? C.amber : tone === 'alert' ? C.ember : tone === 'alertCount' ? alertCountColor(C) : C.text);
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 5, padding: 8, minWidth: 0,
    }}>
      <div style={{
        fontSize: 9.5, lineHeight: '12px', fontWeight: 700, color: C.sub, letterSpacing: '0.08em',
        textTransform: 'uppercase', marginBottom: 3,
        fontFamily: "'Albert Sans', sans-serif", overflowWrap: 'anywhere',
      }}>{label}</div>
      <div style={{
        fontSize: 16, fontWeight: 700, color: valueColor,
        lineHeight: 1.1, marginBottom: 2, fontFamily: "'Albert Sans', sans-serif", overflowWrap: 'anywhere',
      }}>{value}</div>
      <div style={{ fontSize: 10.5, lineHeight: '13px', color: C.muted, fontFamily: "'Albert Sans', sans-serif", overflowWrap: 'anywhere' }}>{sub}</div>
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────────

export function ZoneStatusPanel({
  zone,
  snap,
  metric,
  densitySparkline,
  surgeRate5Min,
  trendUnavailable = false,
  projectionStatus,
  operationName,
  priorityNote,
  activeAlertCount = 0,
  flaggedTokenCount = 0,
  C,
  onClose,
  zones,
  onZoneChange,
}: ZoneStatusPanelProps) {
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const { flags } = useFlags();
  const hasDensity = Boolean(snap);
  const util       = snap?.capacityUtilisation ?? 0;
  const fillColor  = densityColor(util, C);
  const trend      = snap?.trendDirection ?? 'stable';
  const surgeRate  = hasDensity ? (surgeRate5Min ?? null) : null;
  const canFlagToken = currentUser.role === 'crowd_analyst' || currentUser.role === 'command_supervisor';
  const flagLimitReached = flags.length >= 20;

  // Trend badge
  const trendColor  = trendToneColor(trend, C);
  const TrendIcon   = trend === 'rising' ? Icons.ArrowU : trend === 'falling' ? Icons.ArrowD : Icons.ArrowR;
  const trendLabel  = trend === 'rising' ? 'Rising' : trend === 'falling' ? 'Falling' : 'Stable';

  // Composition comes from the approved aggregate projection only.
  const occupancy = snap?.occupancyCount ?? 0;
  const hasComposition = Boolean(metric && metric.compositionTotal !== null);
  const compTotal  = metric?.compositionTotal ?? 0;
  const compNew    = metric?.newEntrants ?? 0;
  const compRepeat = metric?.repeatVisitors ?? 0;
  const compDwell  = metric?.dwellOverThreshold ?? 0;
  const freshnessColor = projectionStatusColor(projectionStatus, C);

  const hasSparkline = (densitySparkline?.length ?? 0) >= 2;
  // Status follows live utilisation; a historical playback point derives it from that point only.
  const displayStatus = trendUnavailable ? resolveZoneStatus(null, snap) : resolveZoneStatus(metric, snap);

  return (
    <div style={{
      width: 340, flexShrink: 0,
      background: C.panel,
      borderLeft: `1px solid ${C.border2}`,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Albert Sans', sans-serif",
    }}>

      {priorityNote && (
        <div style={{
          padding: '7px 18px', background: C.faint, borderBottom: `1px solid ${C.border2}`,
          fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', color: C.sub,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 9 }}>
            Highest priority
          </span>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{priorityNote}</span>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border2}` }}>
        {/* Top row: "Zone" label + status pill + close button */}
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', marginBottom: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: C.muted,
            }}>Zone</span>
            <Pill color={zoneStatusColor(displayStatus, C)}>
              <StatusDot color="#fff" size={5} pulse={displayStatus === 'alert'} />
              {zoneStatusLabel(displayStatus).toLowerCase()}
            </Pill>
          </div>
          <button
            onClick={onClose}
            aria-label="Close zone panel"
            style={{
              background: 'transparent', border: 'none',
              color: C.muted, cursor: 'pointer', padding: 2, flexShrink: 0,
            }}
          >
            <Icons.X size={14} />
          </button>
        </div>

        {/* Zone name */}
        <div style={{
          fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {zone.name}
        </div>

        {/* Code · type label row */}
        <div style={{ fontSize: FONT_SIZE.sm, lineHeight: '13.3px', color: C.muted, letterSpacing: LETTER_SPACING.field }}>
          {zoneTypeCode(zone.type)} · {zoneTypeLabel(zone.type)}
        </div>
        {operationName && (
          <div style={{
            marginTop: 6,
            fontSize: FONT_SIZE.sm, lineHeight: '13.3px',
            color: C.sub,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            Operation: {operationName}
          </div>
        )}
      </div>

      {/* ── Zone selector ───────────────────────────────────────────────── */}
      <div style={{ padding: '10px 18px', borderBottom: `1px solid ${C.border}` }}>
        <select
          value={zone.id}
          onChange={e => onZoneChange(e.target.value)}
          style={{
            width: '100%', padding: '7px 10px', fontSize: 11.5,
            background: C.surface, color: C.text,
            border: `1px solid ${C.border3}`, borderRadius: 4,
            fontFamily: "'Albert Sans', sans-serif", outline: 'none', cursor: 'pointer',
          }}
        >
          {zones.map(z => (
            <option key={z.id} value={z.id}>{zoneTypeCode(z.type)} - {z.name}</option>
          ))}
        </select>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>

        {/* ── Current density label ─────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <KLabel C={C} tone="sub">Current Density</KLabel>
          <span style={{
            fontSize: FONT_SIZE.micro,
            color: freshnessColor,
            background: projectionStatus === 'fresh' ? C.greenDim : projectionStatus === 'error' ? C.emberDim : C.faint,
            border: `1px solid ${freshnessColor}44`,
            borderRadius: 999,
            padding: '3px 7px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}>
            {projectionStatusLabel(projectionStatus)}
          </span>
        </div>

        {/* ── Density hero row ──────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 8 }}>
          <span style={{
            fontSize: 42, fontWeight: 900, color: fillColor, lineHeight: 1,
            fontVariantNumeric: 'tabular-nums', fontFamily: "'Albert Sans', sans-serif",
          }}>{hasDensity ? util : '--'}</span>
          {hasDensity && <span style={{ fontSize: 18, color: C.muted, marginBottom: 4, marginLeft: 2 }}>%</span>}
          {/* Trend badge */}
          <div style={{
            marginLeft: 'auto', fontSize: 11, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 4,
            color: trendUnavailable ? C.muted : trendColor, fontFamily: "'Albert Sans', sans-serif",
          }}>
            {!hasDensity ? 'Awaiting data' : trendUnavailable ? 'Trend unavailable' : <><TrendIcon size={12} />{trendLabel}</>}
          </div>
        </div>

        {/* ── Capacity bar section ──────────────────────────────────────── */}
        <div style={{ marginBottom: 14 }}>
          {/* Top row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 10.5, color: C.sub }}>
              {hasDensity ? `${occupancy.toLocaleString()} persons` : 'No current occupancy data'} · {zone.capacity.toLocaleString()} cap.
            </span>
            <span style={{ fontSize: 11, lineHeight: '14px', color: C.sub, fontFamily: 'monospace' }}>{hasDensity ? `${util}%` : '--'}</span>
          </div>

          {/* Bar */}
          <div style={{
            height: 8, background: C.faint, borderRadius: 4,
            overflow: 'hidden', position: 'relative',
          }}>
            <div style={{
              width: `${Math.min(util, 100)}%`, height: '100%',
              background: fillColor, borderRadius: 4,
              transition: 'width 600ms ease, background 300ms ease',
            }} />
            {/* 85% threshold marker */}
            <div style={{
              position: 'absolute', left: '85%', top: -2, bottom: -2,
              width: 1.5, background: C.text, opacity: 0.4,
            }} />
          </div>

          {/* Footer text */}
          <div style={{
            fontSize: 10.5, lineHeight: '12.6px', color: C.muted, marginTop: 3,
            fontFamily: 'monospace',
          }}>
            alert threshold @ 85% · warning @ 70%
          </div>
        </div>

        {/* ── Sparkline ─────────────────────────────────────────────────── */}
        <KLabel C={C} tone="sub">Density — Last 60 Min</KLabel>
        {hasSparkline ? (
          <ZoneSparkline data={densitySparkline as number[]} color={fillColor} C={C} />
        ) : (
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 5, padding: '12px 10px', marginBottom: 4,
            color: C.muted, fontSize: 11, lineHeight: 1.4,
          }}>
            No density history available for this zone in the last 60 minutes.
          </div>
        )}

        {/* ── MiniStat responsive grid ───────────────────────────────────── */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))',
          gap: 8, marginTop: 14, marginBottom: 14,
        }}>
          <MiniStat
            label="Surge Rate"
            value={surgeRate === null ? '--' : `${surgeRate >= 0 ? '+' : '-'}${Math.abs(surgeRate).toFixed(1)}%`}
            sub={surgeRate === null ? 'awaiting data' : 'last 5 min'}
            tone={surgeRate !== null && surgeRate > 15 ? 'warning' : 'normal'}
            color={surgeRate === null ? C.muted : surgeRate > 30 ? C.critical : surgeRate > 15 ? C.amber : C.green}
            C={C}
          />
          <MiniStat
            label="Risk Level"
            value={zone.riskLevel}
            sub="zone config"
            tone={zone.riskLevel === 'critical' ? 'alert' : 'normal'}
            color={
              zone.riskLevel === 'critical' ? C.critical
                : zone.riskLevel === 'high' ? C.ember
                : zone.riskLevel === 'medium' ? C.amber
                : C.green
            }
            C={C}
          />
          <MiniStat
            label="Active Alerts"
            value={String(activeAlertCount)}
            sub="alerts"
            tone={activeAlertCount > 0 ? 'alertCount' : 'normal'}
            C={C}
          />
          <MiniStat
            label="Flagged Tokens"
            value={String(flaggedTokenCount)}
            sub="in zone"
            tone={flaggedTokenCount > 0 ? 'warning' : 'normal'}
            C={C}
          />
        </div>

        {/* ── Anonymous Composition ─────────────────────────────────────── */}
        <KLabel C={C} tone="sub">Anonymous Composition</KLabel>
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 6, padding: 10, marginBottom: 14,
        }}>
          {hasComposition ? (
            <>
              {[
                { label: 'Total observed',    value: compTotal,  color: C.green },
                { label: 'New entrants',     value: compNew,    color: C.teal  },
                { label: 'Repeat visitors',  value: compRepeat, color: C.amber },
                { label: 'Dwell (extended)', value: compDwell,  color: C.ember },
              ].map(row => (
                <div key={row.label} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: 6,
                }}>
                  <StatusDot color={row.color} size={6} />
                  <span style={{ flex: 1, fontSize: 11, color: C.sub }}>{row.label}</span>
                  <span style={{
                    fontSize: 11.5, fontWeight: 700, color: C.text,
                    fontFamily: 'monospace',
                  }}>{row.value.toLocaleString()}</span>
                </div>
              ))}
              <div style={{
                fontSize: 10, color: C.muted, marginTop: 6,
                paddingTop: 6, borderTop: `1px solid ${C.border}`,
                lineHeight: '14px',
              }}>
                Anonymous aggregate counts only. No identity fields exposed.
              </div>
              {canFlagToken && (
                <span
                  title={flagLimitReached ? 'Session flag limit reached (20 max)' : undefined}
                  style={{ display: 'block', marginTop: 8 }}
                >
                  <Btn
                    C={C}
                    variant="ghost"
                    icon={<Icons.Flag size={SIZE.iconSm} />}
                    disabled={flagLimitReached}
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      color: C.amber,
                      borderColor: `${C.amber}55`,
                    }}
                    onClick={() => navigate('/flags', { state: { openCreateModal: true, zoneId: zone.id } })}
                  >
                    Flag a Token
                  </Btn>
                </span>
              )}
            </>
          ) : (
            <span style={{ fontSize: 11, color: C.muted }}>No current composition data.</span>
          )}
        </div>

      </div>
    </div>
  );
}
